// Taxonomy for area-tagged property photos. Renter-facing labels in
// PHOTO_AREA_LABELS; the modal nav renders sections in PHOTO_AREA_ORDER.
// Untagged photos render under ADDITIONAL_PHOTOS_LABEL, that bucket is
// never selectable by owners; it's the migration fallback.
// Numbered bathrooms (Oct 29) work like numbered bedrooms: offered only when
// the home has more than one; the plain tag stands in for one of them.

export type PhotoArea =
  | "kitchen"
  | "bathroom"
  | "bathroom_1"
  | "bathroom_2"
  | "bathroom_3"
  | "bathroom_4"
  | "bathroom_5"
  | "bathroom_6"
  | "bathroom_7"
  | "bathroom_8"
  | "bedroom"
  | "bedroom_1"
  | "bedroom_2"
  | "bedroom_3"
  | "bedroom_4"
  | "bedroom_5"
  | "bedroom_6"
  | "bedroom_7"
  | "bedroom_8"
  | "living"
  | "workspace"
  | "laundry"
  | "patio"
  | "outdoor"
  | "pool"
  | "rooftop"
  | "view"
  | "exterior"
  | "parking";

export const PHOTO_AREA_LABELS: Record<PhotoArea, string> = {
  kitchen: "Full kitchen",
  bathroom: "Full bathroom",
  bathroom_1: "Bathroom 1",
  bathroom_2: "Bathroom 2",
  bathroom_3: "Bathroom 3",
  bathroom_4: "Bathroom 4",
  bathroom_5: "Bathroom 5",
  bathroom_6: "Bathroom 6",
  bathroom_7: "Bathroom 7",
  bathroom_8: "Bathroom 8",
  bedroom: "Bedroom",
  bedroom_1: "Bedroom 1",
  bedroom_2: "Bedroom 2",
  bedroom_3: "Bedroom 3",
  bedroom_4: "Bedroom 4",
  bedroom_5: "Bedroom 5",
  bedroom_6: "Bedroom 6",
  bedroom_7: "Bedroom 7",
  bedroom_8: "Bedroom 8",
  living: "Living area",
  workspace: "Workspace",
  laundry: "Laundry",
  patio: "Patio",
  outdoor: "Outdoor dining",
  pool: "Pool",
  rooftop: "Rooftop",
  view: "View",
  exterior: "Exterior",
  parking: "Parking",
};

export const PHOTO_AREA_ORDER: PhotoArea[] = [
  "kitchen",
  "bathroom",
  "bathroom_1",
  "bathroom_2",
  "bathroom_3",
  "bathroom_4",
  "bathroom_5",
  "bathroom_6",
  "bathroom_7",
  "bathroom_8",
  "bedroom",
  "bedroom_1",
  "bedroom_2",
  "bedroom_3",
  "bedroom_4",
  "bedroom_5",
  "bedroom_6",
  "bedroom_7",
  "bedroom_8",
  "living",
  "workspace",
  "laundry",
  "patio",
  "outdoor",
  "pool",
  "rooftop",
  "view",
  "exterior",
  "parking",
];

export const ADDITIONAL_PHOTOS_LABEL = "Additional photos";

/** Who set a photo's tag. Absent on photos tagged before Oct 29 (the owner). */
export type PhotoAreaSource = "owner" | "ai";

export interface PhotoEntry {
  url: string;
  area: PhotoArea | null;
  /**
   * "ai" when the photo analyzer filled the tag in (Oct 29: numbered
   * bedrooms and bathrooms from the owner's bedroom details and the upload
   * order); "owner" once the owner picks or clears a tag, which the analyzer
   * then never touches again. Absent = the owner's, from before.
   */
  areaSource?: PhotoAreaSource;
  /** What the photo shows, written by the photo analyzer
   *  (supabase/functions/analyze-listing-photos). Absent until analyzed. */
  alt?: string;
  /**
   * Storage path inside the private `property-originals` bucket holding the
   * full-resolution, pre-compression original of this photo (EXIF intact).
   * Set by photoUpload.ts from the wizard and the edit page; older photos may
   * lack it, and then `url` is the best stored version. Readers that don't
   * need it ignore it, and the DB `image_urls` sync trigger reads only `url`.
   */
  originalPath?: string;
}

const PHOTO_AREA_SET = new Set<PhotoArea>(PHOTO_AREA_ORDER);

export const isPhotoArea = (v: unknown): v is PhotoArea =>
  typeof v === "string" && PHOTO_AREA_SET.has(v as PhotoArea);

/**
 * Group photos into a stable, order-preserving map keyed by area. Returns
 * an array of { area, label, photos } entries:
 *  - One entry per area that has at least one photo, in PHOTO_AREA_ORDER.
 *  - A trailing "Additional photos" entry only when at least one photo has
 *    area === null.
 *
 * Within each group, photos preserve their original `photos[]` order.
 * `photoIndex` on each item is the position in the original flat array
 * (used by the zoom view to navigate across all photos).
 */
export interface GroupedPhotoItem {
  photo: PhotoEntry;
  photoIndex: number;
}

export interface PhotoGroup {
  /** Area key for tagged groups, or "additional" for the catch-all. */
  key: PhotoArea | "additional";
  label: string;
  items: GroupedPhotoItem[];
}

export const groupPhotosByArea = (photos: PhotoEntry[]): PhotoGroup[] => {
  const taggedBuckets = new Map<PhotoArea, GroupedPhotoItem[]>();
  const untagged: GroupedPhotoItem[] = [];

  photos.forEach((photo, photoIndex) => {
    if (photo.area && isPhotoArea(photo.area)) {
      const existing = taggedBuckets.get(photo.area);
      if (existing) existing.push({ photo, photoIndex });
      else taggedBuckets.set(photo.area, [{ photo, photoIndex }]);
    } else {
      untagged.push({ photo, photoIndex });
    }
  });

  const groups: PhotoGroup[] = [];
  for (const area of PHOTO_AREA_ORDER) {
    const items = taggedBuckets.get(area);
    if (items && items.length > 0) {
      groups.push({ key: area, label: PHOTO_AREA_LABELS[area], items });
    }
  }
  if (untagged.length > 0) {
    groups.push({ key: "additional", label: ADDITIONAL_PHOTOS_LABEL, items: untagged });
  }
  return groups;
};

export const countUntagged = (photos: PhotoEntry[]): number =>
  photos.reduce((n, p) => (p.area ? n : n + 1), 0);
